(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-scl-root]';
  const TOTAL_STEPS = 6;

  const labels = {
    fixtureType: {
      pendant: 'Pendant Light',
      chandelier: 'Chandelier',
      'wall-light': 'Wall Light',
      'table-lamp': 'Table Lamp',
      'floor-lamp': 'Floor Lamp'
    },
    materials: {
      'natural-rattan': 'Natural Rattan',
      willow: 'Willow',
      'natural-wood': 'Natural Wood',
      'dark-wood': 'Dark Wood',
      'rattan-wood': 'Rattan & Wood',
      fabric: 'Fabric',
      'brass-accents': 'Brass Accents',
      'iron-hardware': 'Iron Hardware'
    },
    shape: {
      dome: 'Dome',
      drum: 'Drum',
      globe: 'Globe',
      bell: 'Bell',
      petal: 'Petal',
      layered: 'Layered',
      sculptural: 'Sculptural',
      custom: 'Custom Shape'
    },
    sizePreset: {
      small: 'Small',
      medium: 'Medium',
      large: 'Large',
      oversized: 'Oversized',
      custom: 'Custom Size'
    },
    finish: {
      natural: 'Natural',
      'light-wood': 'Light Wood',
      'dark-wood': 'Dark Wood',
      'matte-black': 'Matte Black',
      'antique-brass': 'Antique Brass',
      'brushed-brass': 'Brushed Brass',
      custom: 'Custom Finish'
    },
    lightTemperature: {
      'warm-white': 'Warm White',
      'neutral-white': 'Neutral White',
      'no-preference': 'No Preference'
    },
    mounting: {
      hardwired: 'Hardwired',
      'plug-in': 'Plug-in',
      'not-sure': 'Not Sure'
    },
    hangingType: {
      cord: 'Cord',
      chain: 'Chain',
      rod: 'Rod',
      'not-sure': 'Not Sure'
    },
    voltage: {
      '110-120v': '110-120V',
      '220-240v': '220-240V',
      'not-sure': 'Not Sure'
    },
    projectType: {
      residential: 'Residential',
      'interior-design-project': 'Interior Design Project',
      hospitality: 'Hospitality',
      restaurant: 'Restaurant',
      retail: 'Retail',
      commercial: 'Commercial',
      other: 'Other'
    },
    roomType: {
      'dining-room': 'Dining Room',
      'living-room': 'Living Room',
      bedroom: 'Bedroom',
      kitchen: 'Kitchen',
      entryway: 'Entryway',
      hallway: 'Hallway',
      staircase: 'Staircase',
      office: 'Office',
      restaurant: 'Restaurant',
      hotel: 'Hotel',
      other: 'Other'
    },
    timeline: {
      'as-soon-as-possible': 'As soon as possible',
      'within-1-month': 'Within 1 month',
      '1-3-months': '1-3 months',
      '3-6-months': '3-6 months',
      'still-planning': 'Still planning'
    },
    budget: {
      'under-500': 'Under $500',
      '500-1000': '$500-$1,000',
      '1000-2500': '$1,000-$2,500',
      '2500-5000': '$2,500-$5,000',
      '5000-plus': '$5,000+',
      'not-sure': 'Not sure'
    }
  };

  function createDefaultState() {
    return {
      currentStep: 0,
      fixtureType: '',
      materials: [],
      shape: '',
      customShape: '',
      sizePreset: '',
      dimensions: { unit: 'in', diameter: '', width: '', height: '', depth: '' },
      roomDimensions: { width: '', length: '', ceilingHeight: '' },
      finish: '',
      customFinish: '',
      lightTemperature: '',
      mounting: '',
      hangingType: '',
      hangingLength: '',
      voltage: '',
      quantity: 1,
      additionalRequirements: '',
      requestedChanges: [],
      productContext: {
        source: '', productId: '', title: '', handle: '', url: '', image: '', variantId: '',
        variantTitle: '', price: '', sku: '', options: ''
      },
      contact: {
        name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        projectType: '',
        roomType: '',
        timeline: '',
        budget: '',
        notes: ''
      },
      referenceLinks: {
        designDrawing: '',
        roomPhotos: '',
        inspirationImages: '',
        floorPlan: ''
      }
    };
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function isPlainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value);
  }

  function mergeState(base, saved) {
    if (!isPlainObject(saved)) return base;
    Object.keys(saved).forEach((key) => {
      if (!(key in base)) return;
      if (isPlainObject(base[key]) && isPlainObject(saved[key])) {
        mergeState(base[key], saved[key]);
      } else if (Array.isArray(base[key])) {
        base[key] = Array.isArray(saved[key]) ? saved[key].slice() : base[key];
      } else {
        base[key] = saved[key];
      }
    });
    return base;
  }

  function getPath(object, path) {
    return path.split('.').reduce((value, key) => (value == null ? undefined : value[key]), object);
  }

  function setPath(object, path, value) {
    const keys = path.split('.');
    const finalKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!isPlainObject(current[key])) current[key] = {};
      return current[key];
    }, object);
    target[finalKey] = value;
  }

  function displayValue(group, value) {
    if (!value) return '';
    return labels[group] && labels[group][value]
      ? labels[group][value]
      : String(value)
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function validUrl(value) {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (error) {
      return false;
    }
  }

  function hasDimension(values) {
    return Object.keys(values)
      .filter((key) => key !== 'unit')
      .some((key) => Number(values[key]) > 0);
  }

  function formatDimensions(values) {
    const unit = values.unit || 'in';
    const names = { diameter: 'Diameter', width: 'Width', height: 'Height', depth: 'Depth' };
    const parts = Object.keys(names)
      .filter((key) => Number(values[key]) > 0)
      .map((key) => `${names[key]} ${values[key]} ${unit}`);
    return parts.join(', ');
  }

  function initRoot(root) {
    if (!root || root.dataset.sclInitialized === 'true') return;
    root.dataset.sclInitialized = 'true';

    const form = root.closest('form');
    if (!form) return;

    const abortController = new AbortController();
    const signal = abortController.signal;
    const storageKey = `solena-custom-lighting-v1-${root.dataset.sectionId || 'page'}`;
    const saveProgress = root.dataset.saveProgress === 'true';
    const isSuccess = root.dataset.submitSuccess === 'true';
    const fields = Array.from(root.querySelectorAll('[data-scl-field]'));
    const choices = Array.from(root.querySelectorAll('[data-scl-choice]'));
    const steps = Array.from(root.querySelectorAll('[data-scl-step]'));
    const nextButton = root.querySelector('[data-scl-next]');
    const previousButton = root.querySelector('[data-scl-previous]');
    const submitButton = root.querySelector('[data-scl-submit]');
    const restartButton = root.querySelector('[data-scl-restart]');
    const formError = root.querySelector('[data-scl-form-error]');
    const progress = root.querySelector('[data-scl-progress]');
    const progressBar = root.querySelector('[data-scl-progress-bar]');
    const stepCount = root.querySelector('[data-scl-step-count]');
    const productContextPanel = root.querySelector('[data-scl-product-context]');
    const requestedChangeInputs = Array.from(root.querySelectorAll('[data-scl-requested-change]'));
    const wizardTitle = root.querySelector('[data-scl-wizard-title]');
    const restartLabel = root.querySelector('[data-scl-restart-label]');
    const query = new URLSearchParams(window.location.search);
    const requestedProductHandle = query.get('product') || '';
    const requestedVariantId = query.get('variant') || '';
    let productMode = query.get('source') === 'product-page' && Boolean(requestedProductHandle);
    let state = createDefaultState();
    let submitting = false;

    fields.forEach((field) => {
      if (field.value !== '') setPath(state, field.dataset.sclField, field.value);
    });

    const initialState = clone(state);

    if (saveProgress) {
      try {
        const saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
        state = mergeState(state, saved);
      } catch (error) {
        try {
          window.localStorage.removeItem(storageKey);
        } catch (storageError) {
          // Storage is optional and can be blocked by the browser.
        }
      }
    }

    if (productMode) {
      state.currentStep = 1;
      state.productContext.source = 'Product Page';
      state.productContext.handle = requestedProductHandle;
      state.productContext.variantId = requestedVariantId;
    }

    state.currentStep = Math.min(Math.max(Number(state.currentStep) || 0, 0), TOTAL_STEPS - 1);

    function persist() {
      if (isSuccess) return;
      try {
        window.localStorage.setItem('solena-custom-lighting-latest', JSON.stringify(state));
        if (saveProgress) window.localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        // Browsing can continue when storage is disabled or full.
      }
    }

    function clearPersisted() {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (error) {
        // Storage may be unavailable in private browsing modes.
      }
    }

    function syncInputs() {
      choices.forEach((input) => {
        const path = input.dataset.sclChoice;
        const value = getPath(state, path);
        input.checked = Array.isArray(value) ? value.includes(input.value) : String(value) === input.value;
      });

      fields.forEach((field) => {
        const value = getPath(state, field.dataset.sclField);
        const normalized = value == null ? '' : String(value);
        if (field.value !== normalized) field.value = normalized;
      });
      requestedChangeInputs.forEach((input) => {
        input.checked = state.requestedChanges.includes(input.value);
      });
    }

    function inferFixtureType(product) {
      const value = `${product.product_type || ''} ${product.title || ''}`.toLowerCase();
      if (value.includes('wall')) return 'wall-light';
      if (value.includes('table')) return 'table-lamp';
      if (value.includes('floor')) return 'floor-lamp';
      if (value.includes('chandelier') || value.includes('ceiling')) return 'chandelier';
      return 'pendant';
    }

    function renderProductContext() {
      if (productContextPanel) productContextPanel.hidden = !productMode;
      root.classList.toggle('is-product-design-mode', productMode);
      if (wizardTitle) wizardTitle.textContent = productMode ? 'Customize This Design' : 'Customize Your Light';
      if (restartLabel) restartLabel.textContent = productMode ? 'Start a New Custom Design' : 'Start Over';
      if (!productMode) return;

      const context = state.productContext;
      const image = root.querySelector('[data-scl-product-image]');
      if (image && context.image) {
        image.src = context.image;
        image.alt = `${context.title} selected for customization`;
        image.hidden = false;
      }
      const values = {
        '[data-scl-product-title]': context.title || 'Customize This Design',
        '[data-scl-product-variant]': context.variantTitle || 'Current selection',
        '[data-scl-product-price]': context.price || '',
        '[data-scl-product-options]': context.options || 'See the original product for current specifications',
      };
      Object.entries(values).forEach(([selector, value]) => {
        const node = root.querySelector(selector);
        if (node) node.textContent = value;
      });
      const link = root.querySelector('[data-scl-product-link]');
      if (link && context.url) link.href = context.url;
    }

    function loadProductContext() {
      if (!productMode) return Promise.resolve();
      return fetch(`/products/${encodeURIComponent(requestedProductHandle)}.js`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Product context could not be loaded.');
          return response.json();
        })
        .then((product) => {
          const variant = product.variants.find((item) => String(item.id) === String(requestedVariantId)) || product.variants[0];
          const optionNames = Array.isArray(product.options) ? product.options : [];
          const optionValues = Array.isArray(variant?.options) ? variant.options : [];
          const options = optionValues.map((value, index) => `${optionNames[index] || `Option ${index + 1}`}: ${value}`).join(', ');
          const currency = root.dataset.currency || 'USD';
          let formattedPrice = '';
          try {
            formattedPrice = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format((variant?.price || product.price || 0) / 100);
          } catch (error) {
            formattedPrice = `$${((variant?.price || product.price || 0) / 100).toFixed(2)}`;
          }
          const productUrl = `${window.location.origin}/products/${product.handle}?variant=${variant.id}`;
          const rawSku = String(variant?.sku || '').trim();
          const normalizedSku = rawSku.toLowerCase();
          const sku = normalizedSku === 'n/a' || normalizedSku === 'default title' ? '' : rawSku;
          state.fixtureType = inferFixtureType(product);
          state.productContext = {
            source: 'Product Page',
            productId: String(product.id || ''),
            title: product.title || '',
            handle: product.handle || requestedProductHandle,
            url: productUrl,
            image: variant?.featured_image?.src || product.featured_image || '',
            variantId: String(variant?.id || ''),
            variantTitle: variant?.title || 'Default Title',
            price: formattedPrice,
            sku,
            options,
          };
          state.currentStep = 1;
          syncInputs();
          renderProductContext();
          renderStep();
        })
        .catch((error) => {
          console.error(error);
          productMode = false;
          state = createDefaultState();
          syncInputs();
          renderProductContext();
          renderStep();
        });
    }

    function syncConditionals() {
      const customShape = root.querySelector('[data-scl-conditional="customShape"]');
      const customSize = root.querySelector('[data-scl-conditional="customSize"]');
      const customFinish = root.querySelector('[data-scl-conditional="customFinish"]');

      if (customShape) customShape.hidden = state.shape !== 'custom';
      if (customSize) customSize.hidden = state.sizePreset !== 'custom';
      if (customFinish) customFinish.hidden = state.finish !== 'custom';

      root.querySelectorAll('[data-scl-unit-label]').forEach((node) => {
        node.textContent = state.dimensions.unit || 'in';
      });
    }

    function summaryText(key) {
      if (key === 'materials') {
        return state.materials.length
          ? state.materials.map((value) => displayValue('materials', value)).join(', ')
          : 'Not selected';
      }
      if (key === 'shape') {
        if (!state.shape) return 'Not selected';
        if (state.shape !== 'custom') return displayValue('shape', state.shape);
        return state.customShape.trim() || state.referenceLinks.designDrawing.trim()
          ? `Custom Shape${state.customShape.trim() ? ` - ${state.customShape.trim()}` : ' - drawing supplied'}`
          : 'Custom Shape';
      }
      if (key === 'sizePreset') {
        if (!state.sizePreset) return 'Not selected';
        if (state.sizePreset !== 'custom') return displayValue('sizePreset', state.sizePreset);
        const dimensions = formatDimensions(state.dimensions);
        return dimensions ? `Custom - ${dimensions}` : 'Custom Size';
      }
      if (key === 'finish') {
        if (!state.finish) return 'Not selected';
        return state.finish === 'custom' && state.customFinish.trim()
          ? `Custom - ${state.customFinish.trim()}`
          : displayValue('finish', state.finish);
      }
      if (key === 'quantity') return String(Math.max(1, Number(state.quantity) || 1));
      const value = getPath(state, key);
      return value ? displayValue(key, value) : 'Not selected';
    }

    function renderSummary() {
      root.querySelectorAll('[data-scl-summary]').forEach((node) => {
        const text = summaryText(node.dataset.sclSummary);
        if (node.textContent !== text) node.textContent = text;
      });
    }

    function roomDimensionsText() {
      const unit = state.dimensions.unit || 'in';
      const names = { width: 'Width', length: 'Length', ceilingHeight: 'Ceiling height' };
      return Object.keys(names)
        .filter((key) => Number(state.roomDimensions[key]) > 0)
        .map((key) => `${names[key]} ${state.roomDimensions[key]} ${unit}`)
        .join(', ');
    }

    function requestPayload() {
      return {
        source: state.productContext.source,
        product: clone(state.productContext),
        requestedChanges: state.requestedChanges.slice(),
        fixtureType: displayValue('fixtureType', state.fixtureType),
        materials: state.materials.map((value) => displayValue('materials', value)),
        shape: summaryText('shape'),
        designDrawingLink: state.referenceLinks.designDrawing.trim(),
        size: summaryText('sizePreset'),
        roomDimensions: roomDimensionsText(),
        finish: summaryText('finish'),
        lightTemperature: displayValue('lightTemperature', state.lightTemperature),
        mounting: displayValue('mounting', state.mounting),
        hangingType: displayValue('hangingType', state.hangingType),
        hangingLength: state.hangingLength ? `${state.hangingLength} ${state.dimensions.unit}` : '',
        voltage: displayValue('voltage', state.voltage),
        quantity: Math.max(1, Number(state.quantity) || 1),
        additionalRequirements: state.additionalRequirements.trim(),
        contact: {
          name: state.contact.name.trim(),
          email: state.contact.email.trim(),
          phone: state.contact.phone.trim(),
          country: state.contact.country.trim(),
          city: state.contact.city.trim(),
          projectType: displayValue('projectType', state.contact.projectType),
          roomType: displayValue('roomType', state.contact.roomType),
          timeline: displayValue('timeline', state.contact.timeline),
          budget: displayValue('budget', state.contact.budget),
          notes: state.contact.notes.trim()
        },
        referenceLinks: {
          designDrawing: state.referenceLinks.designDrawing.trim(),
          roomPhotos: state.referenceLinks.roomPhotos.trim(),
          inspirationImages: state.referenceLinks.inspirationImages.trim(),
          floorPlan: state.referenceLinks.floorPlan.trim()
        }
      };
    }

    function structuredPayload() {
      const payload = clone(state);
      delete payload.currentStep;
      payload.quantity = Math.max(1, Number(payload.quantity) || 1);
      return payload;
    }

    function updateSubmissionFields() {
      const payload = requestPayload();
      const hiddenValues = {
        fixtureType: payload.fixtureType,
        materials: payload.materials.join(', '),
        shape: payload.shape,
        sizePreset: payload.size,
        finish: payload.finish,
        mounting: payload.mounting,
        lightTemperature: payload.lightTemperature,
        quantity: payload.quantity
      };

      root.querySelectorAll('[data-scl-hidden]').forEach((input) => {
        input.value = hiddenValues[input.dataset.sclHidden] || '';
      });

      const productValues = {
        source: payload.source,
        productId: payload.product.productId,
        title: payload.product.title,
        handle: payload.product.handle,
        url: payload.product.url,
        image: payload.product.image,
        variantId: payload.product.variantId,
        variantTitle: payload.product.variantTitle,
        price: payload.product.price,
        sku: payload.product.sku,
        options: payload.product.options,
        requestedChanges: payload.requestedChanges.join(', ')
      };
      root.querySelectorAll('[data-scl-product-hidden]').forEach((input) => {
        input.value = productValues[input.dataset.sclProductHidden] || '';
      });

      const body = [
        'CUSTOM LIGHTING REQUEST',
        '',
        `Source: ${payload.source || 'Custom Lighting Page'}`,
        `Product ID: ${payload.product.productId || 'Not applicable'}`,
        `Product title: ${payload.product.title || 'New custom design'}`,
        `Product handle: ${payload.product.handle || 'Not applicable'}`,
        `Product URL: ${payload.product.url || 'Not applicable'}`,
        `Product image: ${payload.product.image || 'Not applicable'}`,
        `Variant ID: ${payload.product.variantId || 'Not applicable'}`,
        `Variant title: ${payload.product.variantTitle || 'Not applicable'}`,
        `Current price: ${payload.product.price || 'Not applicable'}`,
        `Current SKU: ${payload.product.sku || 'Not supplied'}`,
        `Current options: ${payload.product.options || 'Not applicable'}`,
        `Requested changes: ${payload.requestedChanges.join(', ') || 'New custom design'}`,
        '',
        `Fixture type: ${payload.fixtureType}`,
        `Materials: ${payload.materials.join(', ')}`,
        `Shape: ${payload.shape}`,
        `Design drawing: ${payload.designDrawingLink || 'Not supplied'}`,
        `Size: ${payload.size}`,
        `Room dimensions: ${payload.roomDimensions || 'Not supplied'}`,
        `Finish: ${payload.finish}`,
        `Light temperature: ${payload.lightTemperature}`,
        `Mounting: ${payload.mounting}`,
        `Hanging type: ${payload.hangingType}`,
        `Hanging length: ${payload.hangingLength || 'Not supplied'}`,
        `Voltage: ${payload.voltage}`,
        `Quantity: ${payload.quantity}`,
        `Additional requirements: ${payload.additionalRequirements || 'None'}`,
        '',
        'PROJECT & CONTACT',
        `Name: ${payload.contact.name}`,
        `Email: ${payload.contact.email}`,
        `Phone / WhatsApp: ${payload.contact.phone || 'Not supplied'}`,
        `Country / Region: ${payload.contact.country}`,
        `City: ${payload.contact.city || 'Not supplied'}`,
        `Project type: ${payload.contact.projectType}`,
        `Room type: ${payload.contact.roomType}`,
        `Timeline: ${payload.contact.timeline}`,
        `Budget: ${payload.contact.budget}`,
        `Notes: ${payload.contact.notes || 'None'}`,
        '',
        'REFERENCE LINKS',
        `Room photos: ${payload.referenceLinks.roomPhotos || 'Not supplied'}`,
        `Inspiration images: ${payload.referenceLinks.inspirationImages || 'Not supplied'}`,
        `Floor plan: ${payload.referenceLinks.floorPlan || 'Not supplied'}`
      ].join('\n');

      const subject = root.querySelector('[data-scl-subject]');
      const bodyField = root.querySelector('[data-scl-contact-body]');
      const jsonField = root.querySelector('[data-scl-request-json]');
      if (subject) subject.value = `Custom Lighting Request - ${payload.contact.name || 'New customer'}`;
      if (bodyField) bodyField.value = body;
      if (jsonField) jsonField.value = JSON.stringify(structuredPayload(), null, 2);
    }

    function clearErrors() {
      steps.forEach((step) => step.classList.remove('is-invalid'));
      root.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
      root.querySelectorAll('[data-scl-step-error]').forEach((node) => {
        node.textContent = '';
      });
      if (formError) formError.textContent = '';
    }

    function validationFor(stepIndex) {
      const result = (message, selector) => ({ valid: false, message, selector });

      if (stepIndex === 0 && !state.fixtureType) {
        return result('Choose the type of light you would like to customize.', '[data-scl-choice="fixtureType"]');
      }
      if (stepIndex === 1 && productMode && !state.requestedChanges.length) {
        return result('Choose at least one part of the current design to change.', '[data-scl-requested-change]');
      }
      if (stepIndex === 1 && (!productMode || state.requestedChanges.includes('Material')) && !state.materials.length) {
        return result('Select at least one preferred material.', '[data-scl-choice="materials"]');
      }
      if (stepIndex === 2) {
        const shapeRequired = !productMode || state.requestedChanges.includes('Shape');
        if (shapeRequired && !state.shape) return result('Choose a shape to continue.', '[data-scl-choice="shape"]');
        if (state.shape === 'custom') {
          const drawing = state.referenceLinks.designDrawing.trim();
          if (!state.customShape.trim() && !drawing) {
            return result('Describe your custom shape or provide an accessible design drawing link.', '[data-scl-field="customShape"]');
          }
          if (drawing && !validUrl(drawing)) {
            return result('Enter a complete http or https link for your design drawing.', '[data-scl-field="referenceLinks.designDrawing"]');
          }
        }
      }
      if (stepIndex === 3) {
        const sizeRequired = !productMode || state.requestedChanges.includes('Size');
        if (sizeRequired && !state.sizePreset) return result('Choose a size to continue.', '[data-scl-choice="sizePreset"]');
        if (state.sizePreset === 'custom' && !hasDimension(state.dimensions)) {
          return result('Enter at least one custom dimension.', '[data-scl-field="dimensions.diameter"]');
        }
      }
      if (stepIndex === 4) {
        const finishRequired = !productMode || state.requestedChanges.includes('Finish') || state.requestedChanges.includes('Color');
        const temperatureRequired = !productMode || state.requestedChanges.includes('Light Temperature');
        const hangingRequired = !productMode || state.requestedChanges.includes('Hanging Length');
        if (finishRequired && !state.finish) return result('Choose a finish.', '[data-scl-choice="finish"]');
        if (state.finish === 'custom' && !state.customFinish.trim()) {
          return result('Describe your preferred custom finish.', '[data-scl-field="customFinish"]');
        }
        if (temperatureRequired && !state.lightTemperature) return result('Choose a light temperature.', '[data-scl-choice="lightTemperature"]');
        if (!productMode && !state.mounting) return result('Choose a mounting method.', '[data-scl-choice="mounting"]');
        if (hangingRequired && !state.hangingType) return result('Choose a hanging type.', '[data-scl-choice="hangingType"]');
        if (!productMode && !state.voltage) return result('Choose a voltage.', '[data-scl-choice="voltage"]');
        if (!Number.isFinite(Number(state.quantity)) || Number(state.quantity) < 1) {
          return result('Quantity must be at least 1.', '[data-scl-field="quantity"]');
        }
      }
      if (stepIndex === 5) {
        if (!state.contact.name.trim()) return result('Enter your full name.', '[data-scl-field="contact.name"]');
        const emailField = root.querySelector('[data-scl-field="contact.email"]');
        if (!state.contact.email.trim() || (emailField && !emailField.checkValidity())) {
          return result('Enter a valid email address.', '[data-scl-field="contact.email"]');
        }
        if (!state.contact.country.trim()) return result('Enter your country or region.', '[data-scl-field="contact.country"]');
        if (!state.contact.projectType) return result('Choose a project type.', '[data-scl-field="contact.projectType"]');
        if (!state.contact.roomType) return result('Choose a room type.', '[data-scl-field="contact.roomType"]');
        if (!state.contact.timeline) return result('Choose a project timeline.', '[data-scl-field="contact.timeline"]');
        if (!state.contact.budget) return result('Choose an approximate budget.', '[data-scl-field="contact.budget"]');
        if (!Number.isFinite(Number(state.quantity)) || Number(state.quantity) < 1) {
          return result('Quantity must be at least 1.', '[data-scl-field="quantity"]');
        }
        const linkPaths = ['designDrawing', 'roomPhotos', 'inspirationImages', 'floorPlan'];
        const invalidLink = linkPaths.find((key) => !validUrl(state.referenceLinks[key].trim()));
        if (invalidLink) {
          return result('Enter complete http or https links for your project references.', `[data-scl-field="referenceLinks.${invalidLink}"]`);
        }
      }

      return { valid: true, message: '', selector: '' };
    }

    function showValidation(stepIndex, validation, shouldFocus) {
      if (validation.valid) return true;
      const step = steps[stepIndex];
      if (step) {
        step.classList.add('is-invalid');
        const message = step.querySelector('[data-scl-step-error]');
        if (message) message.textContent = validation.message;
      }
      const target = validation.selector ? root.querySelector(validation.selector) : null;
      if (target) target.setAttribute('aria-invalid', 'true');
      if (shouldFocus) {
        const isChoice = target && target.matches('input[type="radio"], input[type="checkbox"]');
        const scrollTarget = isChoice ? target.closest('label') : target;
        const focusTarget = target || (step && step.querySelector('legend'));
        if (focusTarget) {
          if (!focusTarget.matches('input, select, textarea, button, a[href]')) focusTarget.tabIndex = -1;
          focusTarget.focus({ preventScroll: true });
          (scrollTarget || focusTarget).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return false;
    }

    function refreshButtons() {
      if (!steps.length) return;
      const validation = validationFor(state.currentStep);
      const isLastStep = state.currentStep === TOTAL_STEPS - 1;
      if (nextButton) nextButton.disabled = isLastStep || !validation.valid;
      if (submitButton) submitButton.disabled = !isLastStep || !validation.valid || submitting;
    }

    function syncStepVisibility() {
      steps.forEach((step, index) => {
        const active = index === state.currentStep;
        step.hidden = !active;
        step.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    function renderStep(options) {
      const settings = options || {};
      syncStepVisibility();
      const visibleStep = productMode ? state.currentStep : state.currentStep + 1;
      const visibleTotal = productMode ? TOTAL_STEPS - 1 : TOTAL_STEPS;
      if (stepCount) stepCount.textContent = `Step ${visibleStep} of ${visibleTotal}`;
      if (progress) progress.setAttribute('aria-valuenow', String(visibleStep));
      if (progress) progress.setAttribute('aria-valuemax', String(visibleTotal));
      if (progressBar) progressBar.style.transform = `scaleX(${visibleStep / visibleTotal})`;
      if (previousButton) previousButton.hidden = productMode ? state.currentStep <= 1 : state.currentStep === 0;
      if (nextButton) nextButton.hidden = state.currentStep === TOTAL_STEPS - 1;
      if (submitButton) submitButton.hidden = state.currentStep !== TOTAL_STEPS - 1;
      syncConditionals();
      renderProductContext();
      renderSummary();
      updateSubmissionFields();
      refreshButtons();
      persist();

      if (settings.scroll && window.matchMedia('(max-width: 989px)').matches) {
        const header = root.querySelector('.scl-wizard__header');
        if (header) header.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function refresh() {
      syncStepVisibility();
      syncConditionals();
      renderSummary();
      updateSubmissionFields();
      refreshButtons();
      persist();
    }

    function handleChoice(input) {
      const path = input.dataset.sclChoice;
      if (path === 'materials') {
        state.materials = choices
          .filter((choice) => choice.dataset.sclChoice === 'materials' && choice.checked)
          .map((choice) => choice.value);
      } else {
        setPath(state, path, input.value);
      }
      clearErrors();
      refresh();
    }

    function handleRequestedChanges() {
      state.requestedChanges = requestedChangeInputs.filter((input) => input.checked).map((input) => input.value);
      clearErrors();
      refresh();
    }

    function choiceForTarget(target) {
      if (!(target instanceof Element)) return null;
      if (target.matches('[data-scl-choice]')) return target;
      const label = target.closest('label');
      return label ? label.querySelector('[data-scl-choice]') : null;
    }

    function restoreChoiceViewport(scrollPosition) {
      const restore = () => {
        if (!root.isConnected) return;
        if (window.scrollX !== scrollPosition.x || window.scrollY !== scrollPosition.y) {
          window.scrollTo(scrollPosition.x, scrollPosition.y);
        }
      };

      restore();
      window.requestAnimationFrame(() => {
        restore();
        window.requestAnimationFrame(() => {
          restore();
        });
      });
      window.setTimeout(restore, 80);
      window.setTimeout(restore, 180);
    }

    function handleField(field) {
      let value = field.value;
      if (field.type === 'number' && field.dataset.sclField === 'quantity') {
        value = Math.max(1, parseInt(value || '1', 10) || 1);
      }
      setPath(state, field.dataset.sclField, value);
      if (field.dataset.sclField === 'quantity') {
        fields
          .filter((item) => item !== field && item.dataset.sclField === 'quantity')
          .forEach((item) => {
            item.value = String(value);
          });
      }
      clearErrors();
      refresh();
    }

    root.addEventListener(
      'click',
      (event) => {
        if (!window.matchMedia('(min-width: 990px)').matches) return;
        const input = choiceForTarget(event.target);
        if (!input || !root.contains(input) || event.detail === 0) return;

        event.preventDefault();
        event.stopPropagation();
        const scrollPosition = { x: window.scrollX, y: window.scrollY };
        input.checked = input.type === 'checkbox' ? !input.checked : true;
        handleChoice(input);
        restoreChoiceViewport(scrollPosition);
      },
      { capture: true, signal }
    );

    choices.forEach((input) => {
      input.addEventListener('click', (event) => event.stopPropagation(), { signal });
      input.addEventListener(
        'change',
        (event) => {
          event.stopPropagation();
          handleChoice(input);
        },
        { signal }
      );
    });

    requestedChangeInputs.forEach((input) => {
      input.addEventListener('change', handleRequestedChanges, { signal });
    });

    fields.forEach((field) => {
      const eventName = field.matches('select') ? 'change' : 'input';
      field.addEventListener(eventName, () => handleField(field), { signal });
      if (eventName !== 'change') field.addEventListener('change', () => handleField(field), { signal });
    });

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        () => {
          clearErrors();
          const validation = validationFor(state.currentStep);
          if (!showValidation(state.currentStep, validation, true)) return;
          state.currentStep = Math.min(state.currentStep + 1, TOTAL_STEPS - 1);
          renderStep({ scroll: true });
        },
        { signal }
      );
    }

    if (previousButton) {
      previousButton.addEventListener(
        'click',
        () => {
          clearErrors();
          state.currentStep = Math.max(state.currentStep - 1, 0);
          renderStep({ scroll: true });
        },
        { signal }
      );
    }

    if (restartButton) {
      restartButton.addEventListener(
        'click',
        () => {
          if (productMode) {
            productMode = false;
            window.history.replaceState({}, '', window.location.pathname);
          }
          state = clone(initialState);
          state.productContext = createDefaultState().productContext;
          state.requestedChanges = [];
          state.currentStep = 0;
          clearPersisted();
          form.reset();
          syncInputs();
          clearErrors();
          renderStep({ scroll: true });
        },
        { signal }
      );
    }

    form.addEventListener(
      'submit',
      (event) => {
        if (submitting) {
          event.preventDefault();
          return;
        }

        clearErrors();
        for (let index = 0; index < TOTAL_STEPS; index += 1) {
          const validation = validationFor(index);
          if (!validation.valid) {
            event.preventDefault();
            state.currentStep = index;
            renderStep({ scroll: true });
            showValidation(index, validation, true);
            return;
          }
        }

        if (navigator.onLine === false) {
          event.preventDefault();
          if (formError) formError.textContent = 'You appear to be offline. Your progress is saved; reconnect and try again.';
          return;
        }

        updateSubmissionFields();
        const analyticsDetail = {
          product_id: state.productContext.productId || '',
          variant_id: state.productContext.variantId || '',
          product_handle: state.productContext.handle || ''
        };
        window.dispatchEvent(new CustomEvent('product_customize_submit', { detail: analyticsDetail }));
        window.dataLayer?.push({ event: 'product_customize_submit', ...analyticsDetail });
        persist();
        submitting = true;
        root.classList.add('is-submitting');
        root.setAttribute('aria-busy', 'true');
        if (submitButton) submitButton.disabled = true;
      },
      { signal }
    );

    root._sclDestroy = function () {
      abortController.abort();
      delete root._sclDestroy;
      delete root._sclRestore;
      delete root.dataset.sclInitialized;
    };

    root._sclRestore = function () {
      submitting = false;
      root.classList.remove('is-submitting');
      root.removeAttribute('aria-busy');
      refreshButtons();
    };

    syncInputs();
    renderSummary();
    renderProductContext();

    if (isSuccess) {
      updateSubmissionFields();
      clearPersisted();
      return;
    }

    if (root.querySelector('[data-scl-server-error]')) state.currentStep = TOTAL_STEPS - 1;
    renderStep();
    loadProductContext();
  }

  function initAll(scope) {
    const target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) initRoot(target);
    target.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function destroyAll(scope) {
    const target = scope || document;
    const roots = [];
    if (target.matches && target.matches(ROOT_SELECTOR)) roots.push(target);
    target.querySelectorAll(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (typeof root._sclDestroy === 'function') root._sclDestroy();
    });
  }

  if (window.SolenaCustomLighting) {
    window.SolenaCustomLighting.initAll(document);
    return;
  }

  window.SolenaCustomLighting = { initAll, destroyAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  window.addEventListener('pageshow', () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (typeof root._sclRestore === 'function') root._sclRestore();
    });
  });
})();
